/**
 * The consultant — one conversation, start to finish
 *
 * A consultant does not hand you four forms. They sit down, ask how the work
 * runs, go and look at the numbers, come back with what they found, and then
 * answer whatever you ask next. This screen is that sequence, in one thread:
 *
 *     interview  →  analysis  →  open questions
 *
 * The interview is not a questionnaire bolted on the front. It is where the
 * agent learns the things the ledger cannot tell it — why a control exists,
 * who must never be chased, what people do when the normal route is too slow —
 * and where a stated belief gets checked against what the records show. A gap
 * there surfaces immediately, in the conversation, because it is usually the
 * most valuable thing the interview produces.
 *
 * Everything the agent says carries its citations. A figure it cannot cite is
 * deleted before it reaches the thread, and what was deleted is shown rather
 * than hidden: a system that quietly drops them looks identical to one that
 * never checked.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppShell } from '../../components/shell/AppShell';
import { Card, PageHead, Tag, Tone } from '../../components/ui';
import {
  AgentView,
  AskResult,
  consultantService,
} from '../services/consultant.service';
import {
  Contradiction,
  ProcessQuestion,
  QuestionSetView,
  ResolutionOutcome,
  processService,
} from '../services/process.service';

type Stage = 'loading' | 'interview' | 'analysing' | 'ready' | 'error';

interface Turn {
  id: string;
  who: 'agent' | 'you';
  /** Plain prose. */
  text: string;
  /** Why the agent is asking, shown smaller beneath the question. */
  aside?: string;
  /** A full agent answer, with everything behind it. */
  result?: AskResult;
  /** What produced it, so a rate-limited question can be repeated as asked. */
  asked?: { text: string; playbookKey?: string };
  /** A disagreement the agent wants settled before it advises on that process. */
  contradiction?: Contradiction;
}

/** One question, with the set it belongs to. */
interface PendingQuestion {
  setKey: string;
  setLabel: string;
  question: ProcessQuestion;
  /** True when this is the last question of its set, so the set can be saved. */
  lastOfSet: boolean;
}

const CONFIDENCE_TONE: Record<string, Tone> = { high: 'g', medium: 'a', low: 'r' };

const REFUSAL_HEADLINE: Record<string, string> = {
  playbook_blocked: 'I will not advise on this yet',
  agent_unavailable: 'I am not active for this company yet',
  no_model: 'No model is configured',
  provider_rate_limited: 'The provider is rate limiting — ask me again in a moment',
  unparseable_response: 'The model would not answer in a usable format',
  tool_budget_exhausted: 'I ran out of lookups',
};

const OUTCOME_LABEL: Record<ResolutionOutcome, string> = {
  explained: 'Both are right — here is why they differ',
  data_accepted: 'The data is right, my number was wrong',
  dismissed: 'The measurement is wrong',
};

const CITATION_TONE = (id: string): Tone =>
  id.startsWith('M') ? 'b' : id.startsWith('P') ? 'p' : id.startsWith('N') ? 'n' : 'k';

function formatValue(value: number | null, unit: string, currency: string | null): string {
  if (value === null) return '—';
  const n = value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (currency) return `${currency} ${n}`;
  return unit && unit !== 'number' ? `${n} ${unit}` : n;
}

/**
 * Lower-case the first word so a trigger reads on from "worth running when".
 * Left alone when the word is an acronym, because "DPO materially below DSO"
 * came out as "dPO".
 */
function uncapitalise(text: string): string {
  const [first] = text.split(' ');
  if (first && first === first.toUpperCase() && first.length > 1) return text;
  return text.charAt(0).toLowerCase() + text.slice(1);
}

let turnSeq = 0;
const nextId = (): string => `t${++turnSeq}`;

/** The agent's own answer, inline in the thread. */
const AgentAnswer: React.FC<{
  result: AskResult;
  /** Offered only for a rate limit, which is the one refusal that clears itself. */
  onRetry?: () => void;
}> = ({ result, onRetry }) => {
  if (result.refused) {
    return (
      <div className="cons-refusal">
        <strong>{REFUSAL_HEADLINE[result.refusalReason ?? ''] ?? 'I cannot answer that'}</strong>
        <p>{result.message}</p>
        {result.refusalReason === 'playbook_blocked' && (
          <p className="cons-small">
            This is not a failure. I can see the numbers, and you have told me
            something about the process that contradicts them. Advice built on a
            disputed picture of the work is worse than no advice, so I am waiting.
          </p>
        )}
        {result.refusalReason === 'provider_rate_limited' && (
          <>
            <p className="cons-small">
              Free provider tiers cap how fast you may ask. Your answers and the
              interview are untouched — only this one question needs repeating.
            </p>
            {onRetry && (
              <div className="mt">
                <button className="btn btn-p" onClick={onRetry}>
                  Ask again
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  if (!result.finding) return null;
  const f = result.finding;
  const e = result.evidence;

  return (
    <div>
      <div className="cons-headline">{f.headline}</div>

      <ul className="cons-claims">
        {f.claims.map((claim, i) => (
          <li key={i}>
            {claim.statement}{' '}
            {claim.citations.map((c) => (
              <Tag key={c} tone={CITATION_TONE(c)}>
                {c}
              </Tag>
            ))}
          </li>
        ))}
      </ul>

      {f.recommendedAction && (
        <p className="cons-action">
          <strong>What I would do:</strong> {f.recommendedAction}
        </p>
      )}

      <div className="cons-meta">
        <Tag tone={CONFIDENCE_TONE[f.confidence] ?? 'a'}>{f.confidence} confidence</Tag>{' '}
        <Tag tone="d">as at {result.referenceDate}</Tag>{' '}
        {result.model && (
          <Tag tone={result.model.provisional ? 'a' : 'g'}>{result.model.model}</Tag>
        )}{' '}
        <span className="cons-small">
          {Math.round(result.durationMs / 100) / 10}s ·{' '}
          {result.tokens.input + result.tokens.output} tokens
        </span>
      </div>

      <details className="cons-details">
        <summary>
          What I used — {e.measured.length} measured, {e.knowledge.length} references,{' '}
          {e.signals.length} market signals, {e.process.length} from you
          {f.rejected.length > 0 ? `, ${f.rejected.length} deleted` : ''}
        </summary>

        <div className="cons-ev">
          <div className="cons-ev-h">M · your ledger, through definitions somebody signed</div>
          {e.measured.map((m) => (
            <div key={m.citationId} className="cons-ev-row">
              <Tag tone="b">{m.citationId}</Tag> {m.label}{' '}
              <strong>{formatValue(m.value, m.unit, m.currency)}</strong>
              <div className="cons-small">
                {m.certificationState} · signed by {m.owner ?? 'nobody yet'} ·{' '}
                {m.rowsConsidered} records
              </div>
            </div>
          ))}

          {e.knowledge.length > 0 && (
            <>
              <div className="cons-ev-h">K · standards and methodology</div>
              {e.knowledge.map((k) => (
                <div key={k.citationId} className="cons-ev-row">
                  <Tag tone="k">{k.citationId}</Tag> {k.statement}
                  <div className="cons-small">
                    {k.title}
                    {k.author ? ` · ${k.author}` : ''} · {k.certification}
                  </div>
                </div>
              ))}
            </>
          )}

          {e.signals.length > 0 && (
            <>
              <div className="cons-ev-h">N · conditions outside the company</div>
              {e.signals.map((s) => (
                <div key={s.citationId} className="cons-ev-row">
                  <Tag tone="n">{s.citationId}</Tag> {s.headline}{' '}
                  {s.contested && <Tag tone="r">disputed</Tag>}
                  <div className="cons-small">
                    {s.source} · {s.observedAt} ({s.ageDays}d old) · {s.scope}
                  </div>
                </div>
              ))}
            </>
          )}

          {e.process.length > 0 && (
            <>
              <div className="cons-ev-h">P · what you told me about the work</div>
              {e.process.map((p) => (
                <div key={p.citationId} className="cons-ev-row">
                  <Tag tone="p">{p.citationId}</Tag>{' '}
                  {p.isConstraint && <Tag tone="a">constraint</Tag>} {p.answer}
                  <div className="cons-small">
                    {p.prompt} — {p.statedBy}
                  </div>
                </div>
              ))}
            </>
          )}

          {f.rejected.length > 0 && (
            <>
              <div className="cons-ev-h">Deleted before you saw it</div>
              <div className="cons-small" style={{ marginBottom: 6 }}>
                The model stated these and could not cite them.
              </div>
              {f.rejected.map((r, i) => (
                <div key={i} className="cons-ev-row">
                  <Tag tone="r">{r.reason}</Tag>{' '}
                  <span className="cons-small">{r.statement}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </details>
    </div>
  );
};

export const ConsultantPage: React.FC = () => {
  const [stage, setStage] = useState<Stage>('loading');
  const [agent, setAgent] = useState<AgentView | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [error, setError] = useState<string | null>(null);

  // The interview
  const [queue, setQueue] = useState<PendingQuestion[]>([]);
  const [cursor, setCursor] = useState(0);
  const [draft, setDraft] = useState('');
  const [owner, setOwner] = useState('');
  const [answers, setAnswers] = useState<
    Record<string, Array<{ questionKey: string; value: string | number }>>
  >({});

  // Settling a disagreement, inline
  const [settling, setSettling] = useState<Contradiction | null>(null);
  const [settled, setSettled] = useState<string[]>([]);
  const [outcome, setOutcome] = useState<ResolutionOutcome>('explained');
  const [note, setNote] = useState('');

  // Open questions
  const [question, setQuestion] = useState('');
  /** When each playbook last ran in this session, keyed by playbook. */
  const [ran, setRan] = useState<Record<string, string>>({});
  /** The name step is done — explicitly, not the moment a character is typed. */
  const [introduced, setIntroduced] = useState(false);
  const [busy, setBusy] = useState(false);

  const foot = useRef<HTMLDivElement | null>(null);

  // React's StrictMode runs effects twice in development. Both of these
  // effects say something out loud and one of them pays a provider, so
  // neither may run twice — the symptom was the agent introducing itself
  // twice, and it would have billed two analyses.
  const opened = useRef(false);
  const analysed = useRef(false);

  const say = useCallback((turn: Omit<Turn, 'id'>) => {
    setTurns((t) => [...t, { ...turn, id: nextId() }]);
  }, []);

  useEffect(() => {
    foot.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns, stage, settling]);

  /** Build the interview from whatever has not been answered yet. */
  const buildQueue = (sets: QuestionSetView[]): PendingQuestion[] => {
    const pending: PendingQuestion[] = [];
    for (const set of sets) {
      if (set.completedAt) continue;
      set.questions.forEach((q, i) => {
        pending.push({
          setKey: set.key,
          setLabel: set.label,
          question: q,
          lastOfSet: i === set.questions.length - 1,
        });
      });
    }
    return pending;
  };

  useEffect(() => {
    if (opened.current) return;
    opened.current = true;

    void (async () => {
      try {
        const [agents, questionData] = await Promise.all([
          consultantService.listAgents(),
          processService.listQuestionSets(),
        ]);

        const finance = agents[0] ?? null;
        setAgent(finance);

        const named = questionData.sets.find((s) => s.ownerLabel)?.ownerLabel;
        if (named) {
          setOwner(named);
          setIntroduced(true);
        }

        const pending = buildQueue(questionData.sets);
        setQueue(pending);

        if (pending.length === 0) {
          say({
            who: 'agent',
            text:
              'We have spoken before, so I already know how your finance work runs ' +
              'and I will not ask you again — change any of it under My answers ' +
              'whenever it stops being true. Let me look at where things stand now.',
          });
          setStage('analysing');
        } else {
          say({
            who: 'agent',
            text:
              `I am the ${finance?.label ?? 'Finance agent'}. Before I advise you on ` +
              'anything, I need to understand how the work actually runs here — I can ' +
              'read your ledger, but it will not tell me why a control exists or who ' +
              'must never be chased. That is where correct arithmetic turns into ' +
              'reckless advice.',
          });
          say({
            who: 'agent',
            text: `${pending.length} questions, then I will go and look at your numbers.`,
          });
          setStage('interview');
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not reach the agent.');
        setStage('error');
      }
    })();
    // Runs once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Ask the agent something and put the answer in the thread. */
  const ask = useCallback(
    async (text: string, playbookKey?: string, echo = true) => {
      if (!agent) return;
      if (echo) say({ who: 'you', text });

      setBusy(true);
      try {
        const result = await consultantService.ask(agent.key, text, playbookKey);
        say({ who: 'agent', text: '', result, asked: { text, playbookKey } });

        if (playbookKey && !result.refused) {
          setRan((r) => ({
            ...r,
            [playbookKey]: new Date().toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
            }),
          }));
        }
      } catch (e) {
        say({
          who: 'agent',
          text:
            e instanceof Error
              ? `I could not answer that: ${e.message}`
              : 'I could not answer that.',
        });
      } finally {
        setBusy(false);
      }
    },
    [agent, say]
  );

  /** The interview is over: go and look, then come back with something. */
  useEffect(() => {
    if (stage !== 'analysing' || !agent) return;
    if (analysed.current) return;
    analysed.current = true;

    void (async () => {
      say({
        who: 'agent',
        text: 'Reading your ledger, the standards, and current market conditions…',
      });

      await ask(
        'Given everything you now know about how this team works, what is the ' +
          'single most important thing to fix in finance, and what is it worth?',
        undefined,
        false
      );

      say({
        who: 'agent',
        text:
          'Ask me anything from here — or run one of my playbooks below, which is ' +
          'how I produce a recommendation somebody can approve.',
      });
      setStage('ready');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, agent]);

  /** End the name step on purpose, and let the agent acknowledge it. */
  const introduce = useCallback(() => {
    const name = owner.trim();
    if (name.length < 2) {
      setError('A name or a role — I will be citing it.');
      return;
    }
    setError(null);
    say({ who: 'you', text: name });
    say({
      who: 'agent',
      text: `Thank you, ${name.split(',')[0]}. Here is the first one.`,
    });
    setIntroduced(true);
  }, [owner, say]);

  const current = queue[cursor];

  /** Record one answer, saving the set when its last question is done. */
  const answer = useCallback(
    async (value: string) => {
      if (!current) return;

      const trimmed = value.trim();
      if (!trimmed && current.question.required) {
        setError('That one matters — I cannot advise around a blank.');
        return;
      }

      setError(null);
      say({ who: 'you', text: trimmed || '(skipped)' });

      const stored = [
        ...(answers[current.setKey] ?? []),
        ...(trimmed
          ? [
              {
                questionKey: current.question.key,
                value:
                  current.question.kind === 'number' ? Number(trimmed) : trimmed,
              },
            ]
          : []),
      ];
      setAnswers((a) => ({ ...a, [current.setKey]: stored }));
      setDraft('');

      if (current.lastOfSet) {
        setBusy(true);
        try {
          const result = await processService.submit(
            current.setKey,
            stored,
            owner.trim() || 'Finance manager'
          );

          for (const c of result.contradictions) {
            say({
              who: 'agent',
              text:
                `One thing before we go on. You said ${c.statedValue}, but the ledger ` +
                `measures ${c.measuredValue} for ${c.metricKey.replace(/_/g, ' ')} — ` +
                `${c.differencePercent}% apart. I am not assuming either of us is ` +
                'wrong: usually it means the exception has become the rule, or ' +
                'something is not being recorded where you think it is. Either way it ' +
                'matters more than the number did.',
              contradiction: c,
            });
          }
        } catch (e) {
          say({
            who: 'agent',
            text:
              e instanceof Error
                ? `I could not save that: ${e.message}`
                : 'I could not save that.',
          });
        } finally {
          setBusy(false);
        }
      }

      const next = cursor + 1;
      setCursor(next);
      if (next >= queue.length) setStage('analysing');
    },
    [current, answers, cursor, queue.length, owner, say]
  );

  const settle = useCallback(async () => {
    if (!settling) return;
    if (note.trim().length < 10) {
      setError('I need the explanation — it is the part I keep and cite later.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await processService.resolve(settling.id, outcome, note.trim());
      say({ who: 'you', text: note.trim() });
      say({
        who: 'agent',
        text:
          'Noted, and I will cite you on it. That process is no longer disputed, so ' +
          'I can advise on it again.',
      });
      setSettled((ids) => [...ids, settling.id]);
      setSettling(null);
      setNote('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }, [settling, outcome, note, say]);

  const progress = useMemo(() => {
    if (stage !== 'interview' || queue.length === 0) return null;
    return `question ${Math.min(cursor + 1, queue.length)} of ${queue.length}`;
  }, [stage, cursor, queue.length]);

  return (
    <AppShell
      title="Consultant"
      sub={
        stage === 'interview'
          ? (progress ?? 'interview')
          : stage === 'analysing'
            ? 'analysing'
            : (agent?.label ?? 'loading')
      }
    >
      <PageHead
        eyebrow="Consultant"
        title="A consultant that cannot make a number up"
        lede="It asks how your work runs, reads your ledger through definitions somebody signed, checks published standards and current market conditions — then tells you what to fix. Any figure it cannot cite is deleted before you see it, and where your account and the records disagree, it declines until that is settled."
      />

      <Card className="mt">
        <div className="cons-thread">
          {turns.map((turn) => (
            <div
              key={turn.id}
              className={turn.who === 'you' ? 'cons-turn cons-you' : 'cons-turn cons-agent'}
            >
              <div className="cons-who">{turn.who === 'you' ? 'You' : 'Finance agent'}</div>
              {turn.text && <div className="cons-text">{turn.text}</div>}
              {turn.aside && <div className="cons-small cons-aside">{turn.aside}</div>}
              {turn.result && (
                <AgentAnswer
                  result={turn.result}
                  onRetry={
                    turn.asked
                      ? () => void ask(turn.asked!.text, turn.asked!.playbookKey, false)
                      : undefined
                  }
                />
              )}

              {turn.contradiction && !settled.includes(turn.contradiction.id) && (
                <div className="cons-inline">
                  {settling?.id === turn.contradiction.id ? (
                    <>
                      {(Object.keys(OUTCOME_LABEL) as ResolutionOutcome[]).map((key) => (
                        <label key={key} className="cons-radio">
                          <input
                            type="radio"
                            name="outcome"
                            checked={outcome === key}
                            onChange={() => setOutcome(key)}
                          />{' '}
                          {OUTCOME_LABEL[key]}
                        </label>
                      ))}
                      <textarea
                        className="inp"
                        rows={3}
                        value={note}
                        placeholder="What explains the gap? e.g. two days is once it reaches me — it sits in the shared inbox before that"
                        onChange={(e) => setNote(e.target.value)}
                      />
                      <div className="mt">
                        <button className="btn btn-p" disabled={busy} onClick={() => void settle()}>
                          Record it
                        </button>{' '}
                        <button
                          className="btn btn-g"
                          disabled={busy}
                          onClick={() => setSettling(null)}
                        >
                          Later
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      className="btn btn-p"
                      onClick={() => {
                        setSettling(turn.contradiction!);
                        setOutcome('explained');
                        setNote('');
                      }}
                    >
                      Settle this now
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}

          {busy && (
            <div className="cons-turn cons-agent">
              <div className="cons-who">Finance agent</div>
              <div className="cons-text cons-small">thinking…</div>
            </div>
          )}

          <div ref={foot} />
        </div>

        {/* ---- the one thing to do next, whatever stage we are at ---- */}

        {stage === 'interview' && current && (
          <div className="cons-ask">
            {!introduced ? (
              <>
                <div className="cons-q">First — who am I speaking to?</div>
                <div className="cons-small cons-aside">
                  I record every answer against a name, and I cite you when one of
                  them changes what I recommend. An unattributed account of a process
                  is a rumour, and I will not act on one.
                </div>
                <input
                  className="inp"
                  placeholder="e.g. Mona Hassan, Finance Manager"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') introduce();
                  }}
                />
                <div className="mt">
                  <button
                    className="btn btn-p"
                    disabled={owner.trim().length < 2}
                    onClick={introduce}
                  >
                    That&rsquo;s me
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="cons-q">
                  {current.question.prompt}
                  {!current.question.required && ' (optional)'}
                </div>
                <div className="cons-small cons-aside">{current.question.why}</div>
                <div className="cons-small">
                  {current.setLabel} · {progress}
                </div>

                {current.question.kind === 'choice' && current.question.choices ? (
                  <select
                    className="inp"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  >
                    <option value="">—</option>
                    {current.question.choices.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                ) : current.question.kind === 'number' ? (
                  <input
                    className="inp"
                    type="number"
                    step="any"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                ) : (
                  <textarea
                    className="inp"
                    rows={3}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                )}

                {current.question.checkAgainst && (
                  <div className="cons-small">
                    I will check this against what your ledger measures. A gap over{' '}
                    {current.question.checkAgainst.tolerancePercent}% is worth talking
                    about.
                  </div>
                )}

                <div className="mt">
                  <button
                    className="btn btn-p"
                    disabled={busy}
                    onClick={() => void answer(draft)}
                  >
                    {current.lastOfSet && cursor === queue.length - 1
                      ? 'Done — go and look'
                      : 'Next'}
                  </button>{' '}
                  {!current.question.required && (
                    <button className="btn btn-g" disabled={busy} onClick={() => void answer('')}>
                      Skip
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {stage === 'ready' && agent && (
          <div className="cons-ask">
            <textarea
              className="inp"
              rows={2}
              value={question}
              placeholder="Ask me anything about your finances…"
              onChange={(e) => setQuestion(e.target.value)}
            />
            <div className="mt">
              <button
                className="btn btn-p"
                disabled={busy || question.trim().length < 3}
                onClick={() => {
                  const q = question.trim();
                  setQuestion('');
                  void ask(q);
                }}
              >
                Ask
              </button>
            </div>

            <div className="cons-pb-h">
              Standing analyses I can run for you
            </div>
            <div className="cons-small cons-aside">
              These are not questions for you to answer — they are the recurring
              judgement calls in your department, and I run each one against your
              live data. Run the same one next month and the answer will be
              different. Running one scopes the question to that process, which is
              what lets me refuse it outright while something about it is disputed.
            </div>

            <div className="cons-playbooks">
              {agent.playbooks.map((p) => {
                const ranAt = ran[p.key];
                return (
                  <button
                    key={p.key}
                    className="cons-pb"
                    disabled={busy}
                    onClick={() => void ask(p.question, p.key)}
                  >
                    <span className="cons-pb-q">{p.question}</span>
                    <span className="cons-pb-m">
                      {p.label}
                      {' · worth running when '}
                      {uncapitalise(p.trigger)}
                    </span>
                    <span className="cons-pb-f">
                      {p.blocked && <Tag tone="r">disputed — I will decline</Tag>}
                      {ranAt && !p.blocked && (
                        <span className="cons-pb-ran">
                          ran at {ranAt} · run it again whenever you like
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {error && <div className="cons-error">{error}</div>}
      </Card>

      <style>{`
        /* Explicit, because this app is light-themed throughout and a panel
           that inherits its colours can end up dark text on dark ground. */
        .cons-thread { display: flex; flex-direction: column; gap: 14px; max-height: 60vh; overflow-y: auto; padding-right: 4px; color: #0f172a; }
        .cons-turn { border-left: 3px solid var(--bd, #e5e7eb); padding: 2px 0 2px 12px; }
        .cons-agent { border-left-color: #2563eb; }
        .cons-you { border-left-color: #94a3b8; }
        .cons-who { font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: #64748b; margin-bottom: 3px; }
        .cons-text { line-height: 1.5; }
        .cons-small { font-size: 12px; color: #475569; line-height: 1.45; }
        .cons-aside { margin-top: 2px; }
        .cons-headline { font-weight: 600; margin-bottom: 6px; line-height: 1.4; }
        .cons-claims { margin: 0 0 8px 0; padding-left: 18px; }
        .cons-claims li { margin-bottom: 5px; line-height: 1.5; }
        .cons-action { margin: 0 0 8px 0; line-height: 1.5; }
        .cons-meta { margin-bottom: 6px; }
        .cons-details { margin-top: 4px; }
        .cons-details summary { cursor: pointer; font-size: 12px; color: #475569; }
        .cons-ev { margin-top: 8px; padding-left: 10px; border-left: 2px solid #f1f5f9; }
        .cons-ev-h { font-size: 11px; letter-spacing: .05em; text-transform: uppercase; color: #64748b; margin: 10px 0 5px; }
        .cons-ev-row { margin-bottom: 6px; font-size: 13px; line-height: 1.45; }
        .cons-refusal { background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 10px 12px; color: #78350f; }
        .cons-refusal strong { color: #7c2d12; }
        .cons-refusal p { margin: 6px 0 0; line-height: 1.5; }
        .cons-inline { margin-top: 10px; }
        .cons-radio { display: block; margin-bottom: 5px; font-size: 13px; }
        .cons-ask { margin-top: 18px; padding-top: 16px; border-top: 1px solid #e5e7eb; }
        .cons-q { font-weight: 600; margin-bottom: 4px; line-height: 1.4; }
        .cons-ask .inp { margin-top: 8px; }
        .cons-pb-h { font-size: 11px; font-weight: 800; letter-spacing: .13em; text-transform: uppercase; color: #64748b; margin: 18px 0 6px; }
        .cons-playbooks { display: grid; gap: 8px; margin-top: 10px; }
        .cons-pb { display: block; width: 100%; text-align: left; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 12px; cursor: pointer; font: inherit; color: #0f172a; }
        .cons-pb:hover:not(:disabled) { border-color: #2563eb; background: #f8fafc; }
        .cons-pb:disabled { opacity: .55; cursor: default; }
        .cons-pb-q { display: block; font-weight: 600; line-height: 1.4; }
        .cons-pb-m { display: block; font-size: 12px; color: #64748b; margin-top: 3px; line-height: 1.4; }
        .cons-pb-f { display: block; margin-top: 5px; }
        .cons-pb-ran { font-size: 12px; color: #15803d; }
        .cons-error { margin-top: 12px; color: #b91c1c; font-size: 13px; }
      `}</style>
    </AppShell>
  );
};

export default ConsultantPage;
